'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Add01Icon, HelpCircleIcon } from '@terra/ui-icons';
import {
  validateInternalNetblock,
  validateInternalNetworkDomain,
  type AssetValidationResult,
} from '@terra/shared-utils';
import { AlertBanner } from '../../../components/alert-banner';
import { Button } from '../../../components/button';
import { Icon } from '../../../components/icon';
import { Tabs } from '../../../components/tabs';
import { Tooltip } from '../../../components/tooltip';
import { SettingsCard } from '../../settings/SettingsCard';
import { SurfaceScopeListContent } from '../../surface-scope/SurfaceScopeListCard';
import { InternalNetworkSignalCategoriesSection } from './InternalNetworkSignalCategoriesSection';
import {
  INTERNAL_NETWORK_SCOPE_LIST_SECTIONS,
  INTERNAL_NETWORK_SCOPE_SECTION,
  isInternalNetworkScopeListSection,
  isInternalNetworkScopeSection,
  type InternalNetworkScopeListSection,
  type InternalNetworkScopeSection,
} from './internal-scope.model';
import type { InternalNetworkScopeDraft } from './useInternalNetworkScopeDraft';

const SCOPE_TABLE_MAX_HEIGHT = '50vh';

const LIST_VALIDATORS: Record<
  InternalNetworkScopeListSection,
  (value: string) => AssetValidationResult
> = {
  domainNames: validateInternalNetworkDomain,
  dcIps: validateInternalNetblock,
  dnsServerIps: validateInternalNetblock,
};

export type InternalNetworkScopeContentProps = InternalNetworkScopeDraft & {
  isReadOnly: boolean;
  isDiscoveryActive: boolean;
  isSaving: boolean;
  attackPathContent: ReactNode;
};

type ScopeTabTriggerProps = {
  value: InternalNetworkScopeSection;
  title: string;
  tooltip: string;
};

function ScopeTabTrigger({ value, title, tooltip }: ScopeTabTriggerProps) {
  return (
    <Tabs.Trigger value={value}>
      <HStack gap={1} alignItems="center">
        {title}
        <Tooltip title={tooltip}>
          <Icon asChild color="fg.muted" width={4} height={4}>
            <HelpCircleIcon />
          </Icon>
        </Tooltip>
      </HStack>
    </Tabs.Trigger>
  );
}

export function InternalNetworkScopeContent({
  lists,
  onListChange,
  signalCategoriesInScope,
  onSignalCategoriesChange,
  isReadOnly,
  isDiscoveryActive,
  isSaving,
  attackPathContent,
}: InternalNetworkScopeContentProps) {
  const t = useTranslations('designSystem.networkSettings.internalScope');
  const tCommon = useTranslations('designSystem.common');
  const [activeSection, setActiveSection] =
    useState<InternalNetworkScopeSection>(
      INTERNAL_NETWORK_SCOPE_SECTION.domainNames,
    );
  const [addDialogSection, setAddDialogSection] =
    useState<InternalNetworkScopeListSection | null>(null);
  const isListDisabled = isReadOnly || isSaving || isDiscoveryActive;
  const addableSection =
    !isListDisabled && isInternalNetworkScopeListSection(activeSection)
      ? activeSection
      : null;

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      {isDiscoveryActive ? (
        <AlertBanner
          kind="attention"
          title={t('discoveryLockedTitle')}
          description={t('discoveryActiveNotice')}
        />
      ) : null}
      <Tabs.Root
        value={activeSection}
        onValueChange={({ value }) => {
          if (isInternalNetworkScopeSection(value)) {
            setActiveSection(value);
          }
        }}
        size="sm"
      >
        <Tabs.List>
          {INTERNAL_NETWORK_SCOPE_LIST_SECTIONS.map((section) => (
            <ScopeTabTrigger
              key={section}
              value={section}
              title={t(`lists.${section}.label`)}
              tooltip={t(`lists.${section}.helper`)}
            />
          ))}
          <ScopeTabTrigger
            value={INTERNAL_NETWORK_SCOPE_SECTION.signalCategories}
            title={t('signalCategoriesTab')}
            tooltip={t('signalCategoriesTabTooltip')}
          />
          <ScopeTabTrigger
            value={INTERNAL_NETWORK_SCOPE_SECTION.attackPathScope}
            title={t('attackPathScopeTab')}
            tooltip={t('attackPathScopeTabTooltip')}
          />
          {addableSection ? (
            <HStack flex={1} justifyContent="flex-end" pb={1}>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setAddDialogSection(addableSection)}
              >
                <Add01Icon size={16} />
                {tCommon('add')}
              </Button>
            </HStack>
          ) : null}
          <Tabs.Indicator />
        </Tabs.List>

        {INTERNAL_NETWORK_SCOPE_LIST_SECTIONS.map((section) => {
          const label = t(`lists.${section}.label`);

          return (
            <Tabs.Content key={section} value={section}>
              <SettingsCard>
                <SurfaceScopeListContent
                  values={lists[section]}
                  onChange={(values) => onListChange({ section, values })}
                  validator={LIST_VALIDATORS[section]}
                  addDialog={{
                    title: `${tCommon('add')} ${label}`,
                    description: t(`lists.${section}.bulkDescription`),
                    label,
                    placeholder: t(`lists.${section}.placeholder`),
                    helperText: t(`lists.${section}.helper`),
                  }}
                  valueColumnTitle={label}
                  searchPlaceholder={t('searchPlaceholder')}
                  emptyStateTitle={t('emptyState')}
                  duplicateErrorMessage={t('duplicateError')}
                  disabled={isListDisabled}
                  tableMaxHeight={SCOPE_TABLE_MAX_HEIGHT}
                  embedded
                  addDialogOpen={addDialogSection === section}
                  onAddDialogOpenChange={(open) =>
                    setAddDialogSection(open ? section : null)
                  }
                />
              </SettingsCard>
            </Tabs.Content>
          );
        })}

        <Tabs.Content value={INTERNAL_NETWORK_SCOPE_SECTION.signalCategories}>
          <InternalNetworkSignalCategoriesSection
            signalCategoriesInScope={signalCategoriesInScope}
            onSignalCategoriesChange={onSignalCategoriesChange}
            isReadOnly={isReadOnly || isDiscoveryActive}
            tableMaxHeight={SCOPE_TABLE_MAX_HEIGHT}
            hideHeader
          />
        </Tabs.Content>

        <Tabs.Content value={INTERNAL_NETWORK_SCOPE_SECTION.attackPathScope}>
          {attackPathContent}
        </Tabs.Content>
      </Tabs.Root>
    </VStack>
  );
}
